import React, { useId, useState } from "react";
import { Headphones, Mail, Phone } from "lucide-react";
import "./supportContactPanel.css";

export interface SupportContact {
  id: string | number;
  name: string;
  role?: string | null;
  email: string;
  phone: string;
  phoneTarget: string;
}

/** The same contact presentation is used for the agent and administrator preview. */
export function SupportContactPanel({
  contacts = [],
  description,
  children,
  className = "",
  busy = false,
}: {
  contacts?: SupportContact[];
  description?: string;
  children?: React.ReactNode;
  className?: string;
  busy?: boolean;
}) {
  const headingId = useId();
  const detailsId = useId();
  const [chosenId, setChosenId] = useState<string | number | undefined>(
    contacts[0]?.id,
  );
  const expandedId = contacts.some((contact) => contact.id === chosenId)
    ? chosenId
    : contacts[0]?.id;

  return (
    <section
      className={`support-contact-panel ${className}`}
      aria-labelledby={headingId}
      aria-busy={busy}
    >
      <h2 className="support-contact-panel__heading" id={headingId}>
        <Headphones size={32} strokeWidth={2} aria-hidden="true" />
        <span>Your support panel</span>
      </h2>
      {description && (
        <p className="support-contact-panel__description">{description}</p>
      )}
      {children}
      {!!contacts.length && (
        <ul className="support-contact-panel__list">
          {contacts.map((contact, index) => {
            const expanded = contact.id === expandedId;
            const initials = contact.name
              .trim()
              .split(/\s+/)
              .filter(Boolean)
              .map((word) => word[0])
              .filter((_, i, parts) => i === 0 || i === parts.length - 1)
              .join("")
              .toUpperCase();
            const mailto = `mailto:${encodeURIComponent(contact.email).replace(/%40/gi, "@")}`;
            const tel = `tel:${contact.phoneTarget}`;
            return (
              <li
                key={contact.id}
                className={`support-contact-card${expanded ? " support-contact-card--expanded" : ""}`}
              >
                <div className="support-contact-card__top">
                  <button
                    type="button"
                    className="support-contact-card__identity"
                    onClick={() => setChosenId(contact.id)}
                    aria-expanded={expanded}
                    aria-controls={`${detailsId}-${index}`}
                    title={`Show contact details for ${contact.name}`}
                  >
                    <span
                      className="support-contact-card__avatar"
                      aria-hidden="true"
                    >
                      {initials}
                    </span>
                    <span className="support-contact-card__person">
                      <span className="support-contact-card__name">
                        {contact.name}
                      </span>
                      {contact.role && (
                        <span className="support-contact-card__role">
                          {contact.role}
                        </span>
                      )}
                    </span>
                  </button>
                  {!expanded && (
                    <div className="support-contact-card__actions">
                      <a
                        href={tel}
                        aria-label={`Call ${contact.name}: ${contact.phone}`}
                        title={`Call ${contact.phone}`}
                      >
                        <Phone size={25} aria-hidden="true" />
                      </a>
                      <a
                        href={mailto}
                        aria-label={`Email ${contact.name}: ${contact.email}`}
                        title={`Email ${contact.email}`}
                      >
                        <Mail size={25} aria-hidden="true" />
                      </a>
                    </div>
                  )}
                </div>
                <div
                  id={`${detailsId}-${index}`}
                  className="support-contact-card__details"
                  hidden={!expanded}
                >
                  {expanded && (
                    <>
                      <p className="support-contact-card__label">
                        Contact details
                      </p>
                      <div className="support-contact-card__links">
                        <a href={tel}>
                          <Phone size={25} aria-hidden="true" />
                          <span>{contact.phone}</span>
                        </a>
                        <a href={mailto}>
                          <Mail size={25} aria-hidden="true" />
                          <span>{contact.email}</span>
                        </a>
                      </div>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
